/**
 * Draft modal — single detailed card per offer, hold-to-choose.
 *
 * The FRONT/BACK flip died 2026-08-02 (first-friend playtest: "flip
 * mechanism isn't that important... all of the info is on the back so
 * it can just be the main" — the same call the 2026-05-13 layout
 * session had already agreed on, see DRAFT_CARD_LAYOUT_HANDOFF.md).
 * Each offer renders ONE dossier face: header (thumb + name + gender +
 * lv/type/nature), ability box with description, 6-row stat table with
 * compact IV bars, 4-row rich move table (type/cat/PP/pow), IV-sum
 * footer, and the long-press progress ring. Long-press commits the
 * pick — Premise 8's safety contract holds because the full detail IS
 * what the player is looking at while they hold.
 *
 * Architecture:
 *   - Phaser scene shell owns lifecycle (SHUTDOWN cleanup, scene.wake
 *     hop to WorldScene on commit) + audio cues + DOM mount.
 *   - draftCardLogic.ts (pure logic): ivBarCells, shouldAutoShrinkName,
 *     genderGlyph, genderColorRgba, createSeenCardsTracker.
 *   - draftCardGesture.ts (pure-ish state machine): DraftCardGestureMachine
 *     per card, with hooks → DOM mutations + cross-card lockout.
 *   - gestureRouter.ts onLongPress + onTap drive the timing/drift
 *     detection; the state machine just tracks transitions.
 *
 * One-shot per-session bursts (both fire at mount now — there is no
 * flip to defer the perfect-IV burst behind):
 *   - Egg-move 200 ms type-color glow (offer has isRarePull), via
 *     createSeenCardsTracker().seenFront(id).
 *   - Perfect-IV 200 ms type-color glow, via seenBack(id). All IV=31
 *     cells light simultaneously.
 *
 * Reduced motion: all transitions/bursts/sweeps collapse to instant via
 * the CSS @media (prefers-reduced-motion: reduce) rules in tokens.css.
 * The state machine isn't reduced-motion-aware — that's purely visual.
 */
import Phaser from 'phaser';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { FLAGS } from '../scripts/flagRegistry';
import { careerStore } from '../state/careerStore';
import { audio } from '../systems/audio';
import { NATURE_MODS } from '../data/natures';
import { typeAbbreviation } from '../data/typeAbbreviation';
import { onLongPress, onTap } from '../ui/gestureRouter';
import {
  ivBarCells,
  genderGlyph,
  genderColorRgba,
  createSeenCardsTracker,
  type SeenCardsTracker,
} from './draftCardLogic';
import type { SnapshotHandle } from '../render/snapshotBackdrop';
import {
  DraftCardGestureMachine,
  isAnyCardLongPressing,
} from './draftCardGesture';
import type { DraftOffer } from '../state/draftRoll';
import type { StatKey } from '../data/natures';

const PICK_CONFIRM_MS = 1100;
const FADE_OUT_MS = 220;
/** Per design doc Premise 8 — long-press hold to commit. */
const LONG_PRESS_THRESHOLD_MS = 600;
/** Per design doc Premise 8 — 16 px outer hit-buffer for the hold. */
const LONG_PRESS_DRIFT_PX = 16;
/** Per design doc Premise 11 — tap window distinguishing tap-vs-hold. */
const TAP_MAX_DURATION_MS = 200;

/** Per-stat labels for the BACK card's 6-row IV table. Keeps the order
 *  consistent with cartridge stat readouts. */
const STAT_LABEL: Record<StatKey, string> = {
  atk: 'Atk',
  def: 'Def',
  spa: 'SpA',
  spd: 'SpD',
  spe: 'Spe',
};
const STAT_ROW_LABELS: ReadonlyArray<{ key: 'hp' | StatKey; label: string }> = [
  { key: 'hp', label: 'HP' },
  { key: 'atk', label: 'Atk' },
  { key: 'def', label: 'Def' },
  { key: 'spa', label: 'SpA' },
  { key: 'spd', label: 'SpD' },
  { key: 'spe', label: 'Spe' },
];
/** Compact IV bar cell count on the BACK card (design doc Premise 2 —
 *  the BACK stats column is ~144 px wide, narrower than the canonical
 *  16-cell FullSheet variant). */
const BACK_BAR_CELLS = 10;
/** Per-card stagger for the reveal animation. Card 0 = 0 ms, card 1 =
 *  100 ms, card 2 = 200 ms, … */
const REVEAL_STAGGER_MS = 100;
/** Reveal animation total duration for `count` cards — the last card
 *  (delay (count-1) * stagger) finishes its IV-summary fade 1100 ms
 *  after its own start (900 ms entry + 200 ms fade; design doc rev 3
 *  Premise 9). After this timer fires, the cards transition to
 *  data-reveal='done'. Derived from the offer count since pools roll
 *  2-card (Route 1) through 3-card modals. */
const revealTotalMs = (count: number): number =>
  Math.max(0, count - 1) * REVEAL_STAGGER_MS + 1100;

export interface DraftSceneData {
  title: string;
  offers: DraftOffer[];
  /** Glass-Over-World PR 2: WorldScene's frozen snapshot of the world
   *  at the moment the draft fired. transferOwnership'd in create() so
   *  the DOM-glass cards blur the frozen world. Null on capture
   *  failure → fall back to `#0E1014`. */
  snapshotHandle?: SnapshotHandle | null;
}

interface CardHandles {
  root: HTMLDivElement;
  face: HTMLDivElement;
  ringFill: SVGRectElement;
  machine: DraftCardGestureMachine;
  disposeLongPress: () => void;
  disposeTap: () => void;
}

function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

export class DraftScene extends Phaser.Scene {
  private params!: DraftSceneData;
  private modalRoot: HTMLDivElement | null = null;
  private cardsRow: HTMLDivElement | null = null;
  private cards: CardHandles[] = [];
  /** One-shot tracker per scene mount — gates the egg-move + perfect-IV
   *  bursts to once per card per modal (both fire at mount since the
   *  flip died). Reset on each new modal. */
  private seenCards: SeenCardsTracker | null = null;
  private picking = false;
  /** True from scene mount until the reveal animation completes (either
   *  naturally at revealTotalMs or via tap-to-skip pointerdown).
   *  While true, the gesture machines are in REVEALING state and any
   *  pointerdown over the modal triggers skip. Premise 9. */
  private revealActive = true;
  /** Pointer ids that triggered a tap-to-skip during reveal. The
   *  modal capture-phase pointerdown listener adds; pointerup /
   *  pointercancel remove. While a pointer is in this set, the
   *  per-card onLongPress.onStart and onTap cb both bail — that
   *  pointer is consumed and must not arm long-press or flip. Premise
   *  9 — "Long-press timer arms only after the snap". */
  private consumedPointerIds = new Set<number>();
  /** Capture-phase listeners on the modal root. Retained as fields
   *  so the SHUTDOWN teardown can remove them cleanly. */
  private onModalPointerDown: ((e: PointerEvent) => void) | null = null;
  private onModalPointerUp: ((e: PointerEvent) => void) | null = null;
  private onModalPointerCancel: ((e: PointerEvent) => void) | null = null;

  constructor() {
    super('DraftScene');
  }

  init(data: DraftSceneData): void {
    this.params = data;
    this.cards = [];
    this.picking = false;
    this.seenCards = createSeenCardsTracker();
    this.revealActive = true;
    this.consumedPointerIds = new Set<number>();
  }

  create(): void {
    // Glass-Over-World PR 2: claim the WorldScene-parented snapshot as
    // our FIRST act so the DOM-glass cards blur the frozen world
    // underneath. Eng-locked #4. Fall back to the prior dark fill if
    // the capture failed.
    if (this.params.snapshotHandle) {
      this.params.snapshotHandle.transferOwnership(this);
    } else {
      this.cameras.main.setBackgroundColor('#0E1014');
    }

    this.mountDOM();
    void this.patchAsyncTypeData();

    if (this.params.offers.some((o) => o.isRarePull)) {
      audio.playSfx('egg_pull');
    }

    // Schedule natural reveal completion. After revealTotalMs, the
    // cards transition to data-reveal='done' (no animations); the
    // gesture machines transition out of REVEALING. Pointerdown-skip
    // (handled below) short-circuits this timer.
    this.time.delayedCall(revealTotalMs(this.params.offers.length), () =>
      this.completeReveal('natural'),
    );

    // Lab-flow redesign (2026-08-02): the prePickedSpecies tap-on-ball
    // entry path is gone — the draft always mounts with the full
    // reveal; balls are set dressing. (data-prepicked CSS retired with
    // it.)

    this.scale.on('resize', this.handleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  /**
   * End the reveal phase. Two callers:
   *   - 'natural' — the revealTotalMs timer fired without interruption.
   *   - 'skip'    — a pointerdown landed during reveal (handled in the
   *                 capture-phase modal listener below).
   *
   * Both paths advance every gesture machine from REVEALING to
   * IDLE and toggle the cards-row data-reveal attribute so CSS
   * stops the animations. Idempotent — second calls no-op.
   */
  private completeReveal(reason: 'natural' | 'skip'): void {
    if (!this.revealActive) return;
    this.revealActive = false;
    this.cardsRow?.setAttribute('data-reveal', reason === 'skip' ? 'skip' : 'done');
    for (const card of this.cards) card.machine.revealComplete();
  }

  // ─── DOM mount ───────────────────────────────────────────────────────

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'draft-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', this.params.title);

    const title = document.createElement('h1');
    title.className = 'draft-modal__title';
    title.textContent = this.params.title;
    root.appendChild(title);

    const cardsRow = document.createElement('div');
    cardsRow.className = 'draft-modal__cards';
    cardsRow.setAttribute('data-reveal', 'running');
    this.applyOrientation(cardsRow);
    root.appendChild(cardsRow);

    for (let i = 0; i < this.params.offers.length; i++) {
      const offer = this.params.offers[i]!;
      const card = this.buildCard(i, offer);
      // Per-card stagger for the reveal animation (Premise 9). Card N
      // starts at t=N×100 ms relative to scene mount.
      card.root.style.setProperty(
        '--reveal-delay',
        `${i * REVEAL_STAGGER_MS}ms`,
      );
      cardsRow.appendChild(card.root);
      this.cards.push(card);
    }

    // Global long-press instruction — single line at the bottom of the
    // modal, applies to all 3 cards. Replaces the per-card "long-press
    // ⏵ choose" hints that were repeated on each BACK footer.
    const globalHint = document.createElement('div');
    globalHint.className = 'draft-modal__hint';
    globalHint.textContent = 'press & hold a card to choose';
    root.appendChild(globalHint);

    gameEl.appendChild(root);
    this.modalRoot = root;
    this.cardsRow = cardsRow;

    // Capture-phase pointerdown on the modal — fires BEFORE the
    // per-card gestureRouter listeners attached in buildCard(). During
    // reveal, the first pointerdown anywhere on the modal triggers
    // skip + marks that pointer as consumed (per Premise 9 — long-
    // press timer arms ONLY after the snap, so the pointer that fired
    // skip must NOT progress to flip / commit on release).
    this.onModalPointerDown = (e: PointerEvent): void => {
      if (!this.revealActive) return;
      this.consumedPointerIds.add(e.pointerId);
      this.completeReveal('skip');
    };
    this.onModalPointerUp = (e: PointerEvent): void => {
      this.consumedPointerIds.delete(e.pointerId);
    };
    this.onModalPointerCancel = (e: PointerEvent): void => {
      this.consumedPointerIds.delete(e.pointerId);
    };
    root.addEventListener('pointerdown', this.onModalPointerDown, true);
    root.addEventListener('pointerup', this.onModalPointerUp, true);
    root.addEventListener('pointercancel', this.onModalPointerCancel, true);
  }

  private applyOrientation(cardsRow: HTMLDivElement): void {
    const w = this.scale.width;
    const h = this.scale.height;
    cardsRow.setAttribute('data-orientation', h > w ? 'portrait' : 'landscape');
  }

  private handleResize = (): void => {
    if (!this.modalRoot) return;
    const cardsRow = this.modalRoot.querySelector<HTMLDivElement>('.draft-modal__cards');
    if (cardsRow) this.applyOrientation(cardsRow);
  };

  /**
   * Build a single card slot. Returns DOM handles + the gesture
   * machine wired to the card's pointer events.
   *
   * Structure:
   *   .draft-card[data-type='grass']   ← outer container
   *     .draft-card__sweep             ← pick-confirmation specular
   *     .draft-card__face              ← the ONE dossier face
   *       (header, ability desc, stats, rich moves, IV-sum footer)
   *     .draft-card__ring              ← long-press progress ring
   */
  private buildCard(slot: number, offer: DraftOffer): CardHandles {
    const root = document.createElement('div');
    root.className = 'draft-card';
    root.setAttribute('data-state', 'focused');
    // Pre-paint a `--type-normal` tint; patchAsyncTypeData overrides
    // later when @pkmn/dex resolves with the real primary type.
    const primaryType = (offer.types[0] ?? 'Normal').toLowerCase();
    root.setAttribute('data-type', primaryType);
    root.style.setProperty('--card-type-tint', `var(--type-${primaryType})`);

    // Specular sweep overlay (idle; animates on data-state='picked').
    const sweep = document.createElement('div');
    sweep.className = 'draft-card__sweep';
    root.appendChild(sweep);

    const { face, ring: ringSvg, ringFill } = this.buildCardFace(offer);
    root.appendChild(face);
    root.appendChild(ringSvg);

    // ─── Gesture wiring ──────────────────────────────────────────────
    //
    // The machine is fed by two gestureRouter primitives attached to
    // the card root. onLongPress drives the 600 ms threshold + drift
    // detection (firing onStart immediately for ring start, onCancel
    // for pre-threshold release, cb for fire). onTap drives the
    // tap-to-flip path (200 ms max hold). The machine handles state
    // transitions; the shell wires DOM mutations via the hooks below.
    const machine = new DraftCardGestureMachine({
      // Cards mount in REVEALING during the offer-reveal animation
      // (PR 2b). The reveal-complete timer / tap-to-skip path
      // transitions every card's machine to IDLE via
      // machine.revealComplete().
      initialState: 'REVEALING',
      hooks: {
        onCommit: () => this.pick(slot),
        onRingStart: () => {
          ringFill.classList.remove('is-cancelling');
          // Force a reflow before adding is-firing so the transition
          // re-runs from the current state. Without this, toggling the
          // class in the same tick as a previous cancel doesn't
          // re-arm the animation.
          void ringFill.getBoundingClientRect();
          ringFill.classList.add('is-firing');
        },
        onRingFire: () => {
          // Ring is now at "full" — keep the visual at full until the
          // pick-confirmation sequence wipes the card. No extra class
          // toggle needed since is-firing's end state is the full ring.
        },
        onRingCancel: () => {
          ringFill.classList.remove('is-firing');
          ringFill.classList.add('is-cancelling');
        },
        requestPointerCapture: (pointerId) => {
          // Best-effort — pointer capture may throw in some test
          // environments (jsdom-free Node, older browsers). Catch
          // silently; the long-press still works without capture, just
          // with slightly less robust drift handling at the geometry
          // boundary.
          try {
            root.setPointerCapture(pointerId);
          } catch {
            // ignore
          }
        },
        releasePointerCapture: (pointerId) => {
          try {
            root.releasePointerCapture(pointerId);
          } catch {
            // ignore
          }
        },
      },
    });

    const disposeLongPress = onLongPress(
      root,
      {
        thresholdMs: LONG_PRESS_THRESHOLD_MS,
        driftPx: LONG_PRESS_DRIFT_PX,
        onStart: (e) => {
          if (this.picking) return;
          // Reveal-skip consumed this pointer — long-press arms only
          // on a fresh pointerdown post-skip (Premise 9).
          if (this.consumedPointerIds.has(e.pointerId)) return;
          // Machine validates state internally for non-IDLE states.
          if (machine.state === 'IDLE') {
            machine.longPressStarted(e.pointerId);
          }
        },
        onCancel: () => {
          // Idempotent — no-op outside LONG_PRESSING.
          machine.longPressCancelled();
        },
      },
      () => {
        if (this.picking) return;
        machine.longPressFired();
      },
    );

    const disposeTap = onTap(
      root,
      {
        maxDurationMs: TAP_MAX_DURATION_MS,
        driftPx: LONG_PRESS_DRIFT_PX,
      },
      (e) => {
        if (this.picking) return;
        // Reveal-skip consumed this pointer — the same pointer must
        // NOT register as a card tap on release. Next pointerdown is
        // free to tap normally.
        if (this.consumedPointerIds.has(e.pointerId)) return;
        // Cross-card lockout: while ANY other card is mid-long-press,
        // taps on THIS card are no-op. (Post-flip, tap() only matters
        // as the reveal-skip transition — kept for symmetry with the
        // modal-level skip listener.)
        if (isAnyCardLongPressing() && !machine.isLongPressing()) return;
        machine.tap();
      },
    );

    // One-shot celebratory bursts — both fire at mount now that the
    // detailed face is the only face (the perfect-IV burst used to
    // wait for the first flip-to-BACK). Respect prefers-reduced-motion
    // via CSS (the @media query suppresses the keyframes).
    if (offer.isRarePull && this.seenCards?.seenFront(offer.id)) {
      face.setAttribute('data-egg-burst', 'active');
    }
    if (this.seenCards?.seenBack(offer.id)) {
      face.setAttribute('data-perfect-burst', 'active');
    }

    return {
      root,
      face,
      ringFill,
      machine,
      disposeLongPress,
      disposeTap,
    };
  }


  /**
   * Build THE card face — the dossier (ex-BACK; the only face since
   * the flip died 2026-08-02).
   *
   * Layout (top → bottom):
   *   1. header  — 32 px sprite + ident block (name+gender / lv·type·nature)
   *   2. ability — type-tinted left-border box: <b>Name</b> — short desc
   *   3. body    — two-column grid:
   *                LEFT (145 px): stats (header row + 6 data rows + IV bars)
   *                RIGHT (1fr):   moves (header row + 4 data rows + 3-letter type)
   *   4. footer  — IV sum
   *   + SVG progress ring overlay (long-press visualization)
   *
   * Move metadata (type / category / PP / power) is patched async in
   * patchAsyncTypeData via @pkmn/dex; the static render shows row
   * names + em-dash placeholders for the metadata cells until the dex
   * chunk resolves. Child class names keep their historical
   * `__back-*` prefix — renaming ~20 CSS hooks would be churn with
   * zero behavior.
   */
  private buildCardFace(offer: DraftOffer): {
    face: HTMLDivElement;
    ring: SVGSVGElement;
    ringFill: SVGRectElement;
  } {
    const face = document.createElement('div');
    face.className = 'draft-card__face';

    // ── 1. Header — sprite + 2-row ident block (name + gender on row 1,
    // ── lv · type · nature with +/- on row 2). Per user 2026-05-13:
    // ── strict no-wrap no-truncate. Splitting name and meta onto their
    // ── own rows guarantees neither overflows the available width.
    const header = document.createElement('div');
    header.className = 'draft-card__back-header';

    const thumb = document.createElement('img');
    thumb.className = 'draft-card__back-thumb';
    thumb.src = `/assets/pokemon/orb/${offer.dexId}.gif`;
    thumb.alt = '';
    thumb.draggable = false;
    header.appendChild(thumb);

    const ident = document.createElement('div');
    ident.className = 'draft-card__back-ident';

    const nameRow = document.createElement('div');
    nameRow.className = 'draft-card__back-name-row';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'draft-card__back-name';
    nameSpan.textContent = offer.displayName;
    nameRow.appendChild(nameSpan);
    const gender = document.createElement('span');
    gender.className = 'draft-card__gender';
    gender.textContent = genderGlyph(offer.gender);
    gender.style.color = genderColorRgba(offer.gender);
    nameRow.appendChild(gender);
    ident.appendChild(nameRow);

    const metaRow = document.createElement('div');
    metaRow.className = 'draft-card__back-meta';
    metaRow.appendChild(document.createTextNode(`Lv ${offer.level} · `));
    const typeSpan = document.createElement('span');
    typeSpan.className = 'draft-card__back-meta-accent';
    typeSpan.textContent = offer.types.join(' / ');
    metaRow.appendChild(typeSpan);
    metaRow.appendChild(document.createTextNode(' · '));
    metaRow.appendChild(buildNatureNode(offer));
    ident.appendChild(metaRow);

    header.appendChild(ident);

    face.appendChild(header);

    // ── 2. Ability box — type-tinted left border, name + desc inline
    const ability = document.createElement('div');
    ability.className = 'draft-card__back-ability';
    const abilityName = document.createElement('b');
    abilityName.textContent = offer.abilityName || titleCase(offer.ability || 'unknown');
    ability.appendChild(abilityName);
    if (offer.abilityDesc) {
      ability.appendChild(document.createTextNode(' — ' + offer.abilityDesc));
    }
    face.appendChild(ability);

    // ── 3. Body — stats LEFT + moves RIGHT
    const body = document.createElement('div');
    body.className = 'draft-card__back-body';

    // Stats column: header row + 6 data rows (label / base / IV / bar)
    const stats = document.createElement('div');
    stats.className = 'draft-card__back-stats';
    const statHeaders: ReadonlyArray<{ text: string; cls: string }> = [
      { text: 'stat', cls: 'draft-card__stat-h draft-card__stat-h--lbl' },
      { text: 'base', cls: 'draft-card__stat-h' },
      { text: 'iv', cls: 'draft-card__stat-h' },
      { text: '', cls: 'draft-card__stat-h' },
    ];
    for (const h of statHeaders) {
      const el = document.createElement('span');
      el.className = h.cls;
      el.textContent = h.text;
      stats.appendChild(el);
    }
    for (const { key, label } of STAT_ROW_LABELS) {
      const lbl = document.createElement('span');
      lbl.className = 'draft-card__stat-lbl';
      lbl.textContent = label;
      stats.appendChild(lbl);

      const base = document.createElement('span');
      base.className = 'draft-card__stat-base';
      base.textContent = String(offer.baseStats[key]);
      stats.appendChild(base);

      const iv = offer.ivs[key];
      const ivEl = document.createElement('span');
      ivEl.className =
        iv === 31
          ? 'draft-card__stat-iv draft-card__stat-iv--perfect'
          : 'draft-card__stat-iv';
      ivEl.textContent = iv === 31 ? '31★' : String(iv);
      stats.appendChild(ivEl);

      const barWrap = document.createElement('div');
      barWrap.className =
        iv === 31
          ? 'draft-card__stat-bar draft-card__stat-bar--perfect'
          : 'draft-card__stat-bar';
      const fill = document.createElement('span');
      // Width as percentage of cells filled out of BACK_BAR_CELLS so the
      // bar visual matches the cell-based ivBarCells formula. Keeps the
      // "iv=1 shows at least a sliver" guarantee from the cell variant.
      const cells = ivBarCells(iv, BACK_BAR_CELLS);
      fill.style.width = `${(cells / BACK_BAR_CELLS) * 100}%`;
      barWrap.appendChild(fill);
      stats.appendChild(barWrap);
    }
    body.appendChild(stats);

    // Moves column: header row + 4 data rows (name / type / cat / pp / pow)
    const moves = document.createElement('div');
    moves.className = 'draft-card__back-moves';
    const moveHeaders: ReadonlyArray<{ text: string; cls: string }> = [
      { text: 'move', cls: 'draft-card__move-h' },
      { text: 'type', cls: 'draft-card__move-h' },
      { text: 'cat', cls: 'draft-card__move-h draft-card__move-h--cat' },
      { text: 'pp', cls: 'draft-card__move-h draft-card__move-h--num' },
      { text: 'pow', cls: 'draft-card__move-h draft-card__move-h--num' },
    ];
    for (const h of moveHeaders) {
      const el = document.createElement('span');
      el.className = h.cls;
      el.textContent = h.text;
      moves.appendChild(el);
    }
    for (let i = 0; i < 4; i++) {
      const moveId = offer.moves[i];
      const moveName = offer.movesDisplay[i];
      const isEmpty = moveName === undefined;
      const isEgg = !isEmpty && !!offer.eggPullMove && moveId === offer.eggPullMove;

      const nameEl = document.createElement('span');
      nameEl.className = isEmpty
        ? 'draft-card__back-move-name draft-card__back-move-name--empty'
        : isEgg
          ? 'draft-card__back-move-name draft-card__back-move-name--egg'
          : 'draft-card__back-move-name';
      nameEl.textContent = isEmpty ? '—' : moveName!;
      // Tag rows so the async metadata patcher can find them by index.
      if (!isEmpty && moveId) {
        nameEl.setAttribute('data-move-id', moveId);
      }
      moves.appendChild(nameEl);

      const typeEl = document.createElement('span');
      typeEl.className = 'draft-card__back-move-type';
      typeEl.textContent = '—';
      if (!isEmpty && moveId) typeEl.setAttribute('data-move-id', moveId);
      moves.appendChild(typeEl);

      const catEl = document.createElement('span');
      catEl.className = 'draft-card__back-move-cat';
      if (!isEmpty && moveId) catEl.setAttribute('data-move-id', moveId);
      moves.appendChild(catEl);

      const ppEl = document.createElement('span');
      ppEl.className = 'draft-card__back-move-pp';
      ppEl.textContent = '—';
      if (!isEmpty && moveId) ppEl.setAttribute('data-move-id', moveId);
      moves.appendChild(ppEl);

      const powEl = document.createElement('span');
      powEl.className = 'draft-card__back-move-pow';
      powEl.textContent = '—';
      if (!isEmpty && moveId) powEl.setAttribute('data-move-id', moveId);
      moves.appendChild(powEl);
    }
    body.appendChild(moves);

    face.appendChild(body);

    // ── 4. Footer — IV sum only. "caught: <map>" was dropped per user
    // ── 2026-05-13 ("i know where i am at the time of the draft"); the
    // ── per-card long-press hint moved to the modal-level global hint
    // ── two PRs ago. IV-sum stays as the single right-aligned strength
    // ── indicator.
    const footer = document.createElement('div');
    footer.className = 'draft-card__back-footer';
    const ivTotal = document.createElement('span');
    ivTotal.className = 'draft-card__back-iv-sum';
    ivTotal.innerHTML = `IV <b>${ivSum_(offer)}</b>/186`;
    footer.appendChild(ivTotal);
    face.appendChild(footer);

    // ── SVG progress ring overlay — constructed here, returned to
    //    buildCard which attaches it to the card root (outside the 3D
    //    flip) so the long-press sweep is visible from either face.
    //    Geometry (x/y/width/height/rx/ry) is set in CSS via SVG
    //    geometry properties so corners match the card's real
    //    border-radius in pixels — no viewBox stretching distorts them.
    //    pathLength normalizes the perimeter to 100 so the CSS
    //    stroke-dasharray transition (0 100 → 100 0) animates a clean
    //    0–100% sweep regardless of the card's actual perimeter.
    const svgNS = 'http://www.w3.org/2000/svg';
    const ring = document.createElementNS(svgNS, 'svg');
    ring.classList.add('draft-card__ring');
    ring.setAttribute('aria-hidden', 'true');
    const ringRect = document.createElementNS(svgNS, 'rect');
    ringRect.setAttribute('class', 'draft-card__ring-fill');
    ringRect.setAttribute('pathLength', '100');
    ring.appendChild(ringRect);

    return { face, ring, ringFill: ringRect };
  }

  // ─── Async type-tint + move metadata patch ───────────────────────────

  /**
   * Two-phase async paint:
   *
   *   1. Card type-tint (--card-type-tint CSS var) from species.types[0].
   *   2. Per-move metadata on the move table: type abbreviation text +
   *      color, cat-dot color, PP value, base-power value (or "—" for
   *      status moves).
   *
   * The static render shows em-dash placeholders for the metadata
   * cells until this resolves; on a warm @pkmn/dex cache (eager-loaded
   * at boot) the patch lands within microseconds, before the user can
   * even read the row.
   */
  private async patchAsyncTypeData(): Promise<void> {
    let dexMod: typeof import('@pkmn/dex');
    try {
      dexMod = await import('@pkmn/dex');
    } catch {
      return;
    }
    const { Dex } = dexMod;
    if (this.cards.length === 0) return;

    for (let i = 0; i < this.cards.length; i++) {
      const offer = this.params.offers[i];
      const card = this.cards[i];
      if (!offer || !card) continue;

      const data = Dex.species.get(offer.species);
      const t = (data?.types?.[0] ?? 'Normal').toLowerCase();
      card.root.setAttribute('data-type', t);
      card.root.style.setProperty('--card-type-tint', `var(--type-${t})`);

      // Per-move metadata, indexed by move id.
      for (const moveId of offer.moves) {
        if (!moveId) continue;
        const move = Dex.moves.get(moveId);
        const moveType = (move?.type ?? 'Normal').toLowerCase();
        const category = (move?.category ?? 'Status').toLowerCase(); // physical|special|status
        const pp = move?.pp ?? 0;
        const power = move?.basePower ?? 0;
        const isStatus = category === 'status' || power === 0;

        // Paint type abbrev + color, cat dot, PP, power.
        const sel = `[data-move-id="${cssAttr(moveId)}"]`;
        const typeCells = card.face.querySelectorAll<HTMLElement>(
          `.draft-card__back-move-type${sel}`,
        );
        for (const cell of Array.from(typeCells)) {
          cell.textContent = typeAbbreviation(moveType);
          cell.style.color = `var(--type-${moveType})`;
        }
        const catCells = card.face.querySelectorAll<HTMLElement>(
          `.draft-card__back-move-cat${sel}`,
        );
        for (const cell of Array.from(catCells)) {
          cell.style.background = catColor(category);
        }
        const ppCells = card.face.querySelectorAll<HTMLElement>(
          `.draft-card__back-move-pp${sel}`,
        );
        for (const cell of Array.from(ppCells)) {
          cell.textContent = pp > 0 ? String(pp) : '—';
        }
        const powCells = card.face.querySelectorAll<HTMLElement>(
          `.draft-card__back-move-pow${sel}`,
        );
        for (const cell of Array.from(powCells)) {
          cell.textContent = isStatus ? '—' : String(power);
        }
      }
    }
  }

  // ─── Pick + commit ───────────────────────────────────────────────────

  private pick(slot: number): void {
    if (this.picking) return;
    this.picking = true;

    audio.playSfx('ball_open');

    // Type-color bloom on the modal backdrop during pick confirmation —
    // 0.15 alpha tint of the picked card's primary type fades in behind
    // the surviving hero card. Matches the Future Cartridge
    // identity-color thesis (saturated type = WHO this Pokémon is).
    const pickedOffer = this.params.offers[slot];
    const pickedType = (pickedOffer?.types[0] ?? 'Normal').toLowerCase();
    if (this.modalRoot) {
      this.modalRoot.style.setProperty('--pick-tint', `var(--type-${pickedType})`);
      this.modalRoot.setAttribute('data-pick-state', 'confirming');
    }

    // Cards-row orientation drives the exit axis for unpicked cards —
    // landscape (row) → slide horizontally away from the picked column,
    // portrait (column) → slide vertically away from the picked row.
    const cardsRow = this.modalRoot?.querySelector<HTMLDivElement>('.draft-modal__cards');
    const orientation = cardsRow?.getAttribute('data-orientation') ?? 'landscape';
    const EXIT_DISTANCE_PX = 96;
    const EXIT_STAGGER_MS = 60;

    for (let i = 0; i < this.cards.length; i++) {
      const c = this.cards[i];
      if (!c) continue;
      if (i === slot) {
        c.root.setAttribute('data-state', 'picked');
        continue;
      }
      const delta = i - slot;
      const sign = delta > 0 ? 1 : -1;
      const dist = Math.abs(delta);
      if (orientation === 'portrait') {
        c.root.style.setProperty('--exit-x', '0px');
        c.root.style.setProperty('--exit-y', `${sign * EXIT_DISTANCE_PX}px`);
      } else {
        c.root.style.setProperty('--exit-x', `${sign * EXIT_DISTANCE_PX}px`);
        c.root.style.setProperty('--exit-y', '0px');
      }
      c.root.style.setProperty('--exit-delay', `${(dist - 1) * EXIT_STAGGER_MS}ms`);
      c.root.setAttribute('data-state', 'dimming');
    }

    this.time.delayedCall(PICK_CONFIRM_MS, () => this.beginExit(slot));
  }

  private beginExit(slot: number): void {
    const offer = this.params.offers[slot];
    if (!offer) return;
    this.commit(offer);
    this.modalRoot?.classList.add('is-closing');
    this.cameras.main.fadeOut(FADE_OUT_MS, 14, 16, 20);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop();
      this.scene.wake('WorldScene');
      // PR 12.5 bug fix: commit() mutates FLAG_HIDE_*_BALL flags (when
      // drafted from Oak's Lab), but WorldScene's NPC sprites were
      // instantiated on lab-entry — they don't auto-rebuild on flag
      // flip. Call refreshNpcVisibility on wake so the pedestal empties
      // visibly post-pick. No-op for zone drafts (no flag-state change).
      const worldScene = this.scene.get('WorldScene') as unknown as {
        refreshNpcVisibility?: () => void;
      } | null;
      worldScene?.refreshNpcVisibility?.();
    });
  }

  private commit(offer: DraftOffer): void {
    runState.addToParty({
      id: offer.id,
      species: offer.species,
      level: offer.level,
      moves: offer.moves,
      nature: offer.nature,
      ivs: offer.ivs,
      ability: offer.ability,
      gender: offer.gender,
      caughtAt: { ...offer.caughtAt },
    });
    runState.markSpeciesDrafted(offer.species);

    runState.appendDrafted({
      id: offer.id,
      species: offer.species,
      displayName: offer.displayName,
      dexId: offer.dexId,
      types: [...offer.types],
      level: offer.level,
      moves: [...offer.moves],
      movesDisplay: [...offer.movesDisplay],
      nature: offer.nature,
      ivs: { ...offer.ivs },
      ability: offer.ability,
      abilityName: offer.abilityName,
      abilityDesc: offer.abilityDesc,
      baseStats: { ...offer.baseStats },
      eggPullMove: offer.eggPullMove,
      isRarePull: offer.isRarePull,
      gender: offer.gender,
      caughtAt: { ...offer.caughtAt },
    });
    const pickedAt = Date.now();
    const draftedZone = runState.getPendingDraft()?.zoneId ?? 'unknown';
    // H8 (PR 11): the starter draft is the only Oak's Lab draft; setting
    // FLAG_STARTER_PICKED here lets onMapEntered's modal gate flip from
    // "fires whenever party is empty" (CLAUDE.md gotcha M3) to the
    // cartridge-faithful one-shot-per-run posture. Reset Run wipes flags
    // (H1) — a post-reset lab re-entry re-fires the modal.
    //
    // H9 (PR 12): also set the 3 ball-hide flags so the lab table reads
    // as empty pedestals post-pick. Cartridge canon: Oak collects all
    // unpicked balls after the player's choice. The 3 flags default-
    // UNSET (balls visible pre-pick) so setting them flips from
    // visible → hidden uniformly.
    if (draftedZone === 'PalletTown_ProfessorOaksLab') {
      runState.setFlag(FLAGS.STARTER_PICKED);
      // PR A3 (eng-review decision 3): hide ONLY the picked species'
      // ball. The opposing-type ball hides later in the labPostPick
      // cinematic (rival takes it). The third unpicked ball leaves the
      // table when the rival exits post-battle (Oak collects — third-
      // playtest lab redesign). Migrated from the prior hide-all-3
      // behavior; REGRESSION test in PR A6.
      const HIDE_FLAG_BY_SPECIES: Record<string, string> = {
        bulbasaur: FLAGS.HIDE_BULBASAUR_BALL,
        charmander: FLAGS.HIDE_CHARMANDER_BALL,
        squirtle: FLAGS.HIDE_SQUIRTLE_BALL,
      };
      const hideFlag = HIDE_FLAG_BY_SPECIES[offer.species];
      if (hideFlag) runState.setFlag(hideFlag);
      // PR A3 (eng-review decision 1): DOES NOT touch the lab scene var
      // anymore. State machine is owned by the cinematic handlers:
      // oakIntercept sets state=2 at monologue-end, labPostPick handler
      // advances to state=3 at handler-end, rivalLab post-battle sets
      // state=4. DraftScene's job is to set STARTER_PICKED + the right
      // ball-hide flag; the WAKE-handler dispatch gate (state==2 &&
      // STARTER_PICKED && !LAB_POST_PICK_DONE) fires labPostPick on the
      // next WAKE without DraftScene needing to write the state.
    }
    for (const other of this.params.offers) {
      if (other.id === offer.id) continue;
      runState.appendSkipped({
        offer: other,
        passedForSpecies: offer.species,
        zoneId: draftedZone,
        pickedAt,
      });
    }

    careerStore.addEntry({
      id: offer.id,
      species: offer.species,
      displayName: offer.displayName,
      dexId: offer.dexId,
      nature: offer.nature,
      ivs: { ...offer.ivs },
      ability: offer.ability,
      abilityName: offer.abilityName,
      abilityDesc: offer.abilityDesc,
      moves: [...offer.moves],
      movesDisplay: [...offer.movesDisplay],
      baseStats: { ...offer.baseStats },
      level: offer.level,
      draftedAt: pickedAt,
      draftedZone,
      diedAt: null,
      diedTo: null,
      diedZone: null,
      levelAtDeath: null,
      gender: offer.gender,
      caughtAt: { ...offer.caughtAt },
    });

    runState.clearPendingDraft();
    saveManager.save();
  }

  // ─── Teardown ────────────────────────────────────────────────────────

  private teardown(): void {
    this.scale.off('resize', this.handleResize, this);
    for (const card of this.cards) {
      card.disposeLongPress();
      card.disposeTap();
      card.machine.destroy();
    }
    if (this.modalRoot) {
      if (this.onModalPointerDown) {
        this.modalRoot.removeEventListener('pointerdown', this.onModalPointerDown, true);
      }
      if (this.onModalPointerUp) {
        this.modalRoot.removeEventListener('pointerup', this.onModalPointerUp, true);
      }
      if (this.onModalPointerCancel) {
        this.modalRoot.removeEventListener('pointercancel', this.onModalPointerCancel, true);
      }
    }
    this.onModalPointerDown = null;
    this.onModalPointerUp = null;
    this.onModalPointerCancel = null;
    this.modalRoot?.remove();
    this.modalRoot = null;
    this.cardsRow = null;
    this.cards = [];
    this.seenCards = null;
    this.consumedPointerIds.clear();
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Build the nature display node — "Bold +Def/-Atk" with the +stat in
 * green and the -stat in red. Neutral natures (hardy/docile/serious/
 * bashful/quirky) render as just the name. Returns a DocumentFragment-
 * like span so the caller can place it inline within a meta row.
 */
function buildNatureNode(offer: DraftOffer): HTMLSpanElement {
  const wrap = document.createElement('span');
  wrap.className = 'draft-card__nature';
  const mod = NATURE_MODS[offer.nature];
  const name = document.createElement('span');
  name.className = 'draft-card__nature-name';
  name.textContent = titleCase(offer.nature);
  wrap.appendChild(name);
  if (mod.plus && mod.minus) {
    wrap.appendChild(document.createTextNode(' '));
    const plus = document.createElement('span');
    plus.className = 'draft-card__nature-up';
    plus.textContent = `+${STAT_LABEL[mod.plus]}`;
    wrap.appendChild(plus);
    wrap.appendChild(document.createTextNode('/'));
    const minus = document.createElement('span');
    minus.className = 'draft-card__nature-dn';
    minus.textContent = `-${STAT_LABEL[mod.minus]}`;
    wrap.appendChild(minus);
  }
  return wrap;
}

function ivSum_(offer: DraftOffer): number {
  const i = offer.ivs;
  return i.hp + i.atk + i.def + i.spa + i.spd + i.spe;
}

/**
 * Map a move category (physical / special / status) to its semantic
 * color. Mirrors the legend used in the layout mockups: red for
 * physical, blue for special, neutral grey for status. Returned as a
 * direct CSS color string so the move-table cat-dot can paint
 * via inline `style.background` (the values resolve to the same design
 * tokens consumed elsewhere).
 */
function catColor(category: string): string {
  if (category === 'physical') return 'var(--err-red)';
  if (category === 'special') return 'var(--info-blue)';
  return 'var(--text-tertiary)';
}

/**
 * Escape a value for safe use inside a CSS attribute selector. Move ids
 * from @pkmn/dex are lowercase ASCII (e.g. "tackle", "tail-whip") so
 * this mostly passes through; the escape exists as a defensive guard
 * against future ids that might contain quotes / brackets.
 */
function cssAttr(s: string): string {
  return s.replace(/(["\\])/g, '\\$1');
}

